import type { HouseholdProfileSummary, HouseholdProfilesResponse } from "@rbim/shared";
import {
  ArrowLeft,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Edit3,
  Home,
  Search,
  Trash2,
  UsersRound,
} from "lucide-react";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { ApiRequestError, apiRequest } from "../api";
import { SupervisorDeletionOtpActions } from "../components/deletion-otp-actions";

export function HouseholdProfilesPage({
  enableUpdateActions = false,
  enableFullRecordEdit = false,
  enableDeleteActions = false,
  enableSupervisorOtpDeleteActions = false,
}: {
  enableUpdateActions?: boolean;
  enableFullRecordEdit?: boolean;
  enableDeleteActions?: boolean;
  enableSupervisorOtpDeleteActions?: boolean;
}) {
  const [response, setResponse] = useState<HouseholdProfilesResponse | null>(null);
  const [draftSearch, setDraftSearch] = useState("");
  const [search, setSearch] = useState("");
  const [barangayId, setBarangayId] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<HouseholdProfileSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    const parameters = new URLSearchParams({ search, page: String(page), pageSize: "10" });
    if (barangayId) parameters.set("barangayId", barangayId);
    setLoading(true);
    setError("");
    apiRequest<HouseholdProfilesResponse>(`/profiles/households?${parameters}`)
      .then((data) => {
        if (!active) return;
        setResponse(data);
        setSelected((current) => current ? data.items.find((item) => item.recordId === current.recordId) ?? null : null);
      })
      .catch((requestError) => {
        if (active) setError(requestError instanceof ApiRequestError ? requestError.message : "Household profiles could not be loaded.");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [barangayId, page, search]);

  function submitSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPage(1);
    setSearch(draftSearch.trim());
  }

  function updateHousehold(updated: HouseholdProfileSummary) {
    setSelected(updated);
    setResponse((current) => current ? {
      ...current,
      items: current.items.map((item) => item.recordId === updated.recordId ? updated : item),
    } : current);
  }

  function removeHousehold(recordId: string) {
    setSelected(null);
    setResponse((current) => current ? {
      ...current,
      items: current.items.filter((item) => item.recordId !== recordId),
      totalItems: Math.max(0, current.totalItems - 1),
    } : current);
  }

  if (selected) {
    return (
      <HouseholdDesktopDetail
        household={selected}
        enableUpdateActions={enableUpdateActions}
        enableFullRecordEdit={enableFullRecordEdit}
        enableDeleteActions={enableDeleteActions}
        enableSupervisorOtpDeleteActions={enableSupervisorOtpDeleteActions}
        onBack={() => setSelected(null)}
        onChanged={updateHousehold}
        onDeleted={removeHousehold}
      />
    );
  }

  return (
    <section className="profile-desktop-page">
      <header className="profile-desktop-header">
        <div className="profile-desktop-header-main">
          <span className="profile-desktop-header-icon"><Home /></span>
          <div>
            <span className="eyebrow">Profiles</span>
            <h1>Household Profiles</h1>
            <p>Submitted RBIM household records prepared for profiles, review, editing, and reports.</p>
          </div>
        </div>
        <span className="profile-date-chip"><CalendarDays />{formatHeaderDate(new Date())}</span>
      </header>

      <form className="profile-desktop-filter-card" onSubmit={submitSearch}>
        <div className="profile-desktop-filter-grid">
          <label>
            <span>Search households</span>
            <span className="profile-desktop-search">
              <Search />
              <input
                value={draftSearch}
                onChange={(event) => setDraftSearch(event.currentTarget.value)}
                placeholder="Search household no., head, code, or barangay..."
              />
            </span>
          </label>
          <label>
            <span>Barangay</span>
            <select value={barangayId} onChange={(event) => { setBarangayId(event.currentTarget.value); setPage(1); }}>
              <option value="">All Barangays</option>
              {response?.barangays.map((barangay) => <option key={barangay.id} value={barangay.id}>{barangay.name}</option>)}
            </select>
          </label>
          <button className="secondary-button" type="submit">Filter</button>
        </div>
      </form>

      {error ? <div className="form-error" role="alert">{error}</div> : null}

      <section className="profile-desktop-table-card">
        <header className="profile-desktop-table-heading">
          <div>
            <h2>Submitted Households</h2>
            <p>{loading ? "Loading submitted household records" : `${response?.totalItems ?? 0} submitted household ${(response?.totalItems ?? 0) === 1 ? "record" : "records"}`}</p>
          </div>
          <span className="status-pill"><Home />{response?.totalItems.toLocaleString() ?? "—"} households</span>
        </header>

        {loading ? <div className="profile-desktop-empty">Loading authorized household records…</div> : response?.items.length ? (
          <div className="profile-desktop-table-wrap">
            <table className="profile-desktop-table">
              <thead>
                <tr>
                  <th>Household ID / No.</th>
                  <th>Household Type</th>
                  <th>Total Members</th>
                  <th>Families</th>
                  <th>Barangay</th>
                  <th>Status</th>
                  <th>Open</th>
                </tr>
              </thead>
              <tbody>
                {response.items.map((household) => (
                  <tr key={household.recordId} onDoubleClick={() => setSelected(household)}>
                    <td>
                      <span className="profile-primary-text">{household.householdCode || "Uncoded household"}</span>
                      <span className="profile-secondary-text">{household.householdHead || household.respondentName || "No household head encoded"}</span>
                    </td>
                    <td>{household.householdType || "Household"}</td>
                    <td>{household.population}</td>
                    <td>{household.families}</td>
                    <td>{household.barangayName}</td>
                    <td><span className="badge available">{formatCode(household.status)}</span></td>
                    <td><button className="profile-open-button" type="button" onClick={() => setSelected(household)}>Open</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <div className="profile-desktop-empty">No submitted households match the current filters.</div>}

        <Pagination page={response?.page ?? 1} totalPages={response?.totalPages ?? 1} total={response?.totalItems ?? 0} onPage={setPage} />
      </section>
    </section>
  );
}

function HouseholdDesktopDetail({
  household,
  enableUpdateActions,
  enableFullRecordEdit,
  enableDeleteActions,
  enableSupervisorOtpDeleteActions,
  onBack,
  onChanged,
  onDeleted,
}: {
  household: HouseholdProfileSummary;
  enableUpdateActions: boolean;
  enableFullRecordEdit: boolean;
  enableDeleteActions: boolean;
  enableSupervisorOtpDeleteActions: boolean;
  onBack(): void;
  onChanged(household: HouseholdProfileSummary): void;
  onDeleted(recordId: string): void;
}) {
  const memberSummary = useMemo(() => `${household.population} ${household.population === 1 ? "member" : "members"} · ${household.families} ${household.families === 1 ? "family" : "families"}`, [household.families, household.population]);

  return (
    <section className="profile-desktop-page">
      <section className="profile-desktop-detail-card">
        <div className="profile-detail-topbar">
          <div className="profile-detail-topbar-left">
            <button className="profile-detail-back" type="button" onClick={onBack} aria-label="Back to household profiles"><ArrowLeft /></button>
            <div><span className="eyebrow">Household details</span><strong>{household.householdCode || "Uncoded household"}</strong></div>
          </div>
          <div className="profile-detail-topbar-actions">
            {enableFullRecordEdit ? <Link className="primary-button" to={`/records/${household.recordId}/edit`}><Edit3 />Edit Household</Link> : null}
          </div>
        </div>

        <div className="profile-detail-hero">
          <div className="profile-detail-hero-main">
            <span className="profile-detail-hero-icon"><UsersRound /></span>
            <div>
              <span className="eyebrow">Household profile</span>
              <h2>{household.householdCode || "Uncoded household"}</h2>
              <p>{household.barangayName} · {memberSummary}</p>
            </div>
          </div>
          <span className="badge available">{formatCode(household.status)}</span>
        </div>

        <div className="profile-detail-grid">
          <Info label="Barangay" value={household.barangayName} />
          <Info label="Household head" value={household.householdHead || "—"} />
          <Info label="Respondent" value={household.respondentName || "—"} />
          <Info label="Address" value={[household.address, household.purok].filter(Boolean).join(", ") || "—"} />
          <Info label="Household type" value={household.householdType || "—"} />
          <Info label="Families" value={String(household.families)} />
          <Info label="Population" value={String(household.population)} />
          <Info label="Source" value={formatCode(household.origin)} />
          <Info label="Record status" value={formatCode(household.status)} />
        </div>

        <section className="profile-members-section">
          <h3>Household Members</h3>
          <div className="profile-member-list">
            {household.members.length ? household.members.map((member) => (
              <div className="profile-member-row" key={member.id}>
                <div>
                  <strong>{member.fullName || "Unnamed member"}</strong>
                  <span>{member.relationshipToHead || "Relationship not encoded"}</span>
                </div>
                <div><strong>{member.residentType || "Classification not encoded"}</strong></div>
              </div>
            )) : <p className="muted-copy">No household members were returned for this record.</p>}
          </div>
        </section>

        {enableUpdateActions || enableFullRecordEdit || enableDeleteActions || enableSupervisorOtpDeleteActions ? (
          <div className="profile-actions-section">
            <RecordHouseholdActions
              household={household}
              canUpdate={enableUpdateActions}
              canFullEdit={enableFullRecordEdit}
              canDelete={enableDeleteActions}
              canSupervisorOtpDelete={enableSupervisorOtpDeleteActions}
              onChanged={onChanged}
              onDeleted={onDeleted}
            />
          </div>
        ) : null}
      </section>
    </section>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return <div className="profile-info-card"><span>{label}</span><strong>{value}</strong></div>;
}

function RecordHouseholdActions({
  household,
  canUpdate,
  canFullEdit,
  canDelete,
  canSupervisorOtpDelete,
  onChanged,
  onDeleted,
}: {
  household: HouseholdProfileSummary;
  canUpdate: boolean;
  canFullEdit: boolean;
  canDelete: boolean;
  canSupervisorOtpDelete: boolean;
  onChanged?(household: HouseholdProfileSummary): void;
  onDeleted?(recordId: string): void;
}) {
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteFormOpen, setDeleteFormOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setMessage("");
    const form = new FormData(event.currentTarget);
    const changes = {
      householdHead: String(form.get("householdHead") ?? "").trim(),
      respondentName: String(form.get("respondentName") ?? "").trim(),
      address: String(form.get("address") ?? "").trim(),
      purok: String(form.get("purok") ?? "").trim(),
      householdType: String(form.get("householdType") ?? "Household") as "Household" | "Institutional Living Quarter",
    };
    if (!window.confirm(`Save permitted household changes for ${household.householdCode}?`)) return;
    try {
      await apiRequest(`/records/${household.recordId}/household`, { method: "PATCH", body: JSON.stringify(changes) });
      onChanged?.({ ...household, ...changes, updatedAt: new Date().toISOString() });
      setEditing(false);
      setMessage("Household information updated and audit logged.");
    } catch (requestError) {
      setError(requestError instanceof ApiRequestError ? requestError.message : "Household information could not be updated.");
    }
  }

  function reviewDeleteRequest() {
    setError("");
    setMessage("");
    if (!deleteReason.trim()) {
      setError("Deletion reason is required.");
      return;
    }
    setDeleteConfirmOpen(true);
  }

  async function deleteRecord() {
    const reason = deleteReason.trim();
    if (!reason) {
      setDeleteConfirmOpen(false);
      setError("Deletion reason is required.");
      return;
    }

    setDeleting(true);
    setError("");
    setMessage("");
    try {
      await apiRequest(`/admin/records/${household.recordId}`, { method: "DELETE", headers: { "x-rbim-deletion-reason": reason } });
      setDeleteConfirmOpen(false);
      setDeleteFormOpen(false);
      setDeleteReason("");
      onDeleted?.(household.recordId);
    } catch (requestError) {
      setError(requestError instanceof ApiRequestError ? requestError.message : "The RBIM record could not be deleted.");
    } finally { setDeleting(false); }
  }

  return <section className="supervisor-resident-actions">
    <h3>Record actions</h3>
    <div className="record-action-row">
      {canFullEdit ? <Link className="primary-button" to={`/records/${household.recordId}/edit`}><Edit3 />Edit Household</Link> : null}
      {canUpdate && !canFullEdit ? <button className="secondary-button" type="button" onClick={() => setEditing((value) => !value)}><Edit3 />Edit Household</button> : null}
      {canDelete ? <button className="danger-button" type="button" disabled={deleting} onClick={() => { setDeleteFormOpen((value) => !value); setDeleteConfirmOpen(false); setError(""); setMessage(""); }}><Trash2 />{deleting ? "Deleting…" : "Delete RBIM record"}</button> : null}
    </div>
    {canDelete && deleteFormOpen ? (
      <div className="governed-delete-panel">
        <div className="governed-delete-heading">
          <div>
            <span className="eyebrow">Governed deletion</span>
            <h4>Delete {household.householdCode || "this household"}?</h4>
            <p>Enter a clear reason. The reason will be written to the RBIM audit trail.</p>
          </div>
        </div>
        <label>
          Deletion reason
          <textarea
            value={deleteReason}
            onChange={(event) => { setDeleteReason(event.currentTarget.value); setDeleteConfirmOpen(false); }}
            maxLength={1000}
            placeholder="Example: Duplicate household record confirmed during validation."
            autoFocus
          />
          <span className="deletion-reason-help">{deleteReason.length}/1000 characters</span>
        </label>
        {!deleteConfirmOpen ? (
          <div className="governed-delete-actions">
            <button type="button" className="secondary-button" disabled={deleting} onClick={() => { setDeleteFormOpen(false); setDeleteReason(""); setError(""); }}>Cancel</button>
            <button type="button" className="danger-button" disabled={deleting} onClick={reviewDeleteRequest}><Trash2 />Review deletion</button>
          </div>
        ) : (
          <div className="governed-delete-confirmation" role="alertdialog" aria-labelledby="governed-delete-confirmation-title">
            <div>
              <span className="eyebrow">Final confirmation</span>
              <h4 id="governed-delete-confirmation-title">Permanently remove this record from active RBIM data?</h4>
              <p>An archived server copy, deletion tombstone, synchronization change, and audit trail will be retained.</p>
            </div>
            <div className="governed-delete-summary">
              <div><span>Household</span><strong>{household.householdCode || "Uncoded household"}</strong></div>
              <div><span>Barangay</span><strong>{household.barangayName}</strong></div>
              <div className="governed-delete-summary-wide"><span>Reason</span><strong>{deleteReason.trim()}</strong></div>
            </div>
            <div className="governed-delete-actions">
              <button type="button" className="secondary-button" disabled={deleting} onClick={() => setDeleteConfirmOpen(false)}>Back</button>
              <button type="button" className="danger-button" disabled={deleting} onClick={() => void deleteRecord()}><Trash2 />{deleting ? "Deleting…" : "Confirm permanent deletion"}</button>
            </div>
          </div>
        )}
      </div>
    ) : null}
    {canSupervisorOtpDelete ? <SupervisorDeletionOtpActions
      targetType="HOUSEHOLD"
      recordId={household.recordId}
      label={household.householdCode || "this household"}
      onDeleted={() => onDeleted?.(household.recordId)}
    /> : null}
    {message ? <div className="success-message" role="status">{message}</div> : null}
    {error ? <div className="form-error" role="alert">{error}</div> : null}
    {canUpdate && editing ? <form className="resident-edit-form" onSubmit={save}>
      <label>Household head<input name="householdHead" defaultValue={household.householdHead} required /></label>
      <label>Respondent<input name="respondentName" defaultValue={household.respondentName} required /></label>
      <label>Address<input name="address" defaultValue={household.address} required /></label>
      <label>Purok<input name="purok" defaultValue={household.purok} /></label>
      <label>Household type<select name="householdType" defaultValue={household.householdType || "Household"}><option value="Household">Household</option><option value="Institutional Living Quarter">Institutional Living Quarter</option></select></label>
      <button className="primary-button">Save household information</button>
    </form> : null}
  </section>;
}

export function Pagination({ page, totalPages, total, onPage }: { page: number; totalPages: number; total: number; onPage(value: number): void }) {
  return <footer className="profile-pagination"><span>{total.toLocaleString()} matching records</span><div><button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="Previous page"><ChevronLeft /></button><span>Page {page} of {totalPages}</span><button type="button" disabled={page >= totalPages} onClick={() => onPage(page + 1)} aria-label="Next page"><ChevronRight /></button></div></footer>;
}

function formatHeaderDate(value: Date) {
  return new Intl.DateTimeFormat("en-PH", { month: "short", day: "numeric", year: "numeric" }).format(value);
}

function formatCode(value: string) {
  return value.replaceAll("_", " ").toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase());
}
